/** Wizard-Logik der Pflege-App — Kalkulation & Positions-Ableitung.
 *  Formeln identisch zur Server-Wahrheit (vertraege_planer._vorschau) und zum
 *  Planer-Wizard: Faktor unsichtbar eingerechnet, optionale zählen nur mit
 *  beauftragt=true. */
import { einsaetze } from "./frequenz";

export type KatalogEintrag = {
  id: string; code: string; titel: string; beschreibung: string;
  kategorie: string; einheit: string; vk_netto: string; kosten_netto: string;
  zeit_min_je_einheit: string; lohnanteil_prozent: string;
  standard_monate: number[];
  /** Gartentypen, die diese Leistung bedient (Mig 092) — im Katalog-Bereich
   *  gepflegt, Grundlage der Vorschläge in Schritt 3. Leer = wird nie
   *  automatisch vorgeschlagen (Anfahrt, Arbeitsstunden, Entsorgung). */
  garten_typen: string[];
  vogelschutz_relevant: boolean; kalibriert: boolean;
  aktiv?: boolean; sort_order?: number;
  /** 1 oder 2 Punkte je Monat im Kalender (Mig 187) — 2 beim Rasenmähen. */
  max_je_monat?: number;
};

export type VertragPosition = {
  id?: string;
  leistung_id: string | null;
  titel_override: string | null;
  menge: number;
  einheit: string;
  einzelpreis_netto: number;
  kosten_snapshot: number;
  zeit_min_snapshot: number;
  lohnanteil_prozent: number;
  intervall: "monatlich" | "x_pro_jahr";
  // Die Monate MIT Mehrfachnennung: [3,3,5] heißt zweimal im März.
  // `anzahl_pro_jahr` gibt es hier nicht mehr — die Spalte wird in der
  // Datenbank abgeleitet (Mig 152) und vom Server nicht mehr geliefert.
  // Wer sie braucht, ruft `einsaetze()` aus lib/frequenz.
  monate: number[];
  optional: boolean;
  beauftragt: boolean;
  material_notiz: string;
  sort_order: number;
  /** Nur Nebenkosten (Mig 187): null = folgt allen Einsätzen, Liste = nur den
   *  Einsätzen dieser Katalogleistungen, [] = Monate von Hand. */
  folgt?: string[] | null;
};

/** Eine modulare Gartenposition (Feedback 24.07.): Typ + Größe + Zustand. */
export type GartenPosition = {
  typ: string;
  groesse: string;
  einheit: string;
  zustand: string;
};

/** Set hinterlegter Typen (Standard-Einheit); „Sonstiges" erlaubt eigenen Typ. */
export const GARTEN_TYPEN: { typ: string; einheit: string }[] = [
  { typ: "Rasenfläche", einheit: "m²" },
  { typ: "Hecke", einheit: "lfm" },
  { typ: "Beet", einheit: "m²" },
  { typ: "Obstbäume", einheit: "Stück" },
  { typ: "Sträucher", einheit: "Stück" },
  { typ: "Große Bäume", einheit: "Stück" },
  { typ: "Teich", einheit: "m²" },
  { typ: "Wege / Pflaster", einheit: "m²" },
  { typ: "Bewässerung", einheit: "Stück" },
  { typ: "Sonstiges", einheit: "" },
];

export type KundeObjekt = {
  id?: string;
  kunde_id?: string;
  positionen?: GartenPosition[];
  zugang_notiz?: string | null;
  foto_pfade?: string[];
  /** Anschrift des gepflegten Grundstücks — sie steht im Vertrag als
   *  Vertragsgegenstand und kann von der Rechnungsanschrift abweichen. */
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
} | null;


export type KalkulationErgebnis = {
  jahres_netto: string; monats_netto: string; jahres_kosten: string;
  jahres_zeit_stunden: string; deckungsbeitrag: string;
  db_prozent: string | null; db_pro_stunde: string | null; lohnanteil_netto_35a: string;
};

const r2 = (n: number) => Math.round(n * 100) / 100;

export function kalkVorschau(positionen: VertragPosition[], faktor: number): KalkulationErgebnis {
  let netto = 0, kosten = 0, zeitMin = 0, lohn = 0;
  for (const p of positionen) {
    if (!(p.beauftragt || !p.optional)) continue;
    const einzelEff = r2(p.einzelpreis_netto * faktor);
    // Aus den Punkten, nicht aus einer Zahl daneben. Dieselbe Rechnung wie
    // `vertraege_planer._position_berechnen` — läuft sie auseinander, zeigt
    // der Wizard eine andere Summe als die Urkunde.
    const anzahl = einsaetze(p);
    const jahres = r2(r2(einzelEff * p.menge) * anzahl);
    netto = r2(netto + jahres);
    kosten = r2(kosten + r2(p.kosten_snapshot * p.menge * anzahl));
    zeitMin += p.zeit_min_snapshot * p.menge * anzahl;
    lohn = r2(lohn + r2((jahres * p.lohnanteil_prozent) / 100));
  }
  const db = r2(netto - kosten);
  const stunden = zeitMin / 60;
  return {
    jahres_netto: netto.toFixed(2),
    monats_netto: r2(netto / 12).toFixed(2),
    jahres_kosten: kosten.toFixed(2),
    jahres_zeit_stunden: stunden.toFixed(2),
    deckungsbeitrag: db.toFixed(2),
    db_prozent: netto > 0 ? r2((db * 100) / netto).toFixed(2) : null,
    db_pro_stunde: stunden > 0 ? r2(db / stunden).toFixed(2) : null,
    lohnanteil_netto_35a: lohn.toFixed(2),
  };
}

/** Wert der geplanten Einsätze ab `abMonat` bis Dezember — netto.
 *
 *  Seit 26.10.2026 kostet das erste Vertragsjahr genau das (Server:
 *  `vertrag_raten.rumpf_netto`), nicht mehr Restmonate/12 des Jahreswerts.
 *  Beginn im November ohne Einsätze bis Dezember heißt: nichts zu zahlen bis
 *  Januar. Gleiche Rundung wie `kalkVorschau` — mit `abMonat = 1` kommt der
 *  Jahreswert heraus. */
export function planNetto(positionen: VertragPosition[], faktor: number, abMonat = 1): number {
  let netto = 0;
  for (const p of positionen) {
    if (!(p.beauftragt || !p.optional)) continue;
    const n = (p.monate || []).filter(m => m >= abMonat && m <= 12).length;
    if (!n) continue;
    netto = r2(netto + r2(r2(r2(p.einzelpreis_netto * faktor) * p.menge) * n));
  }
  return netto;
}

/** Zahl aus einem Größenfeld — Komma gilt als Dezimaltrenner (deutsche
 *  Eingabe), leere und unsinnige Werte zählen nicht mit. */
function groesseAlsZahl(roh: string): number {
  const n = Number(String(roh ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Wie eine Einheit misst: Fläche, Länge, Stück — oder gar nicht am Garten
 *  (Tag, Einsatz, pauschal, Durchgang, Stunde). */
function mass(einheit: string | null | undefined): "flaeche" | "laenge" | "stueck" | null {
  const e = String(einheit || "").trim().toLowerCase().replace(/\./g, "");
  if (["qm", "m²", "m2"].includes(e)) return "flaeche";
  if (["lfm", "m", "lm"].includes(e)) return "laenge";
  if (["stueck", "stück", "stk"].includes(e)) return "stueck";
  return null;
}

/** Die Menge eines Vorschlags: die Gartengröße nur, wenn die Einheit der
 *  Leistung dieselbe Größe misst — m² Rasen für einen Preis je m². Wird je Tag,
 *  Einsatz oder pauschal abgerechnet, ist es EIN Durchgang.
 *
 *  Bis zum 28.09.2026 wurde die Größe immer übernommen: aus 222 m² Beet wurden
 *  222 Tage „Große Gartenpflege" zu 199.800 € je Durchgang, aus 300 m² Rasen
 *  300 Pauschalen (Benjamin: „Zahlen sind falsch"). */
export function mengeFuerEinheit(einheit: string, treffer: GartenPosition[]): number {
  const m = mass(einheit);
  const passend = m ? treffer.filter(p => mass(p.einheit) === m || !p.einheit) : [];
  if (!m || !passend.length) return 1;
  const summe = passend.reduce((s, p) => s + groesseAlsZahl(p.groesse), 0);
  return summe > 0 ? Math.round(summe * 100) / 100 : 1;
}

/** Menge einer Katalogleistung aus dem Gartenprofil: Summe der Größen aller
 *  Positionen, deren Typ die Leistung laut Katalog bedient.
 *
 *  Summe statt „erste Zeile": zwei Hecken von 20 und 15 lfm sind 35 lfm
 *  Heckenschnitt. Vorher gewann die erste Zeile und die zweite fiel still
 *  unter den Tisch — an einer Kalkulation ist das der teuerste Fehler.
 *
 *  Die Zuordnung steht am Katalogeintrag (`garten_typen`), nicht mehr im Code:
 *  so kann Benjamin sie im Katalog-Bereich ändern, ohne dass jemand baut. */
export function mengeAusObjekt(k: KatalogEintrag, objekt: KundeObjekt): number | null {
  const typen = k.garten_typen || [];
  if (!typen.length || !objekt?.positionen?.length) return null;
  const treffer = objekt.positionen
    .filter(p => typen.includes(p.typ.trim()) && groesseAlsZahl(p.groesse) > 0);
  return treffer.length ? mengeFuerEinheit(k.einheit, treffer) : null;
}

export type LeistungsVorschlag = {
  katalog: KatalogEintrag;
  menge: number;
  /** Welche Gartenzeilen den Vorschlag ausgelöst haben — steht als Begründung
   *  am Vorschlag („aus 35 lfm Hecke"), damit die Zahl nachvollziehbar ist. */
  herkunft: string;
  /** Preis für EINEN Durchgang (Menge × Satz × Faktor). Bewusst nicht der
   *  Jahreswert: wie oft im Jahr eine Leistung nötig ist, entscheidet der
   *  Mensch am Garten, nicht der Katalog. */
  je_durchgang: number;
};

/** Passende Leistungen zum erfassten Garten, sortiert nach Preis je Durchgang.
 *
 *  Zwei bewusste Enthaltsamkeiten:
 *  - Es ist ein VORSCHLAG, keine Vorbelegung — die Position landet erst im
 *    Vertrag, wenn sie übernommen wird.
 *  - Es steht KEIN Zeitraum dabei. Ob eine Hecke einmal oder dreimal im Jahr
 *    geschnitten wird, hängt am Garten und am Kunden; ein vorgeschlagener
 *    Monatssatz würde ungeprüft mitlaufen und den Jahreswert erfinden. */
export function vorschlaegeAusGarten(
  katalog: KatalogEintrag[], garten: GartenPosition[], faktor = 1,
): LeistungsVorschlag[] {
  const zeilen = garten.filter(p => p.typ.trim() !== "" && groesseAlsZahl(p.groesse) > 0);
  if (!zeilen.length) return [];
  const out: LeistungsVorschlag[] = [];
  for (const k of katalog) {
    const typen = k.garten_typen || [];
    if (!typen.length) continue;
    const treffer = zeilen.filter(p => typen.includes(p.typ.trim()));
    if (!treffer.length) continue;
    const menge = mengeFuerEinheit(k.einheit, treffer);
    out.push({
      katalog: k,
      menge,
      herkunft: treffer
        .map(p => `${p.groesse.trim()} ${p.einheit || ""} ${p.typ.trim()}`.replace(/\s+/g, " ").trim())
        .join(" + "),
      je_durchgang: Math.round(Number(k.vk_netto) * faktor * menge * 100) / 100,
    });
  }
  return out.sort((a, b) => b.je_durchgang - a.je_durchgang);
}

/**
 * Die Grundausstattung eines neuen Vertrages: je Gartenteil EINE Leistung,
 * gleich mit der Gartengröße als Menge (Benjamin, 28.09.2026: „wenn ich den
 * Garten erfasse, müssten in Schritt 3 gleich diese Zahlen verwendet werden").
 *
 * Welche: die erste im Katalog (Katalogreihenfolge), die den Gartentyp bedient
 * und in derselben Größe rechnet wie die Zeile — 300 m² Rasen → „Rasenpflege
 * mähen" je m², 3 Stück Große Bäume → „Baumschnitt Großbaum" je Stück. Passt
 * keine Einheit, die erste des Typs. Jede Leistung höchstens einmal; der Rest
 * bleibt unter „Passend zu diesem Garten" zum Dazunehmen.
 */
export function grundausstattung(
  katalog: KatalogEintrag[], garten: GartenPosition[], faktor = 1,
): LeistungsVorschlag[] {
  const alle = vorschlaegeAusGarten(katalog, garten, faktor);
  const gewaehlt: LeistungsVorschlag[] = [];
  for (const zeile of garten) {
    if (!zeile.typ.trim() || groesseAlsZahl(zeile.groesse) <= 0) continue;
    const typ = zeile.typ.trim();
    const kandidaten = katalog.filter(k => (k.garten_typen || []).includes(typ));
    const k = kandidaten.find(x => mass(x.einheit) !== null && mass(x.einheit) === mass(zeile.einheit))
      ?? kandidaten[0];
    if (!k || gewaehlt.some(v => v.katalog.id === k.id)) continue;
    const v = alle.find(x => x.katalog.id === k.id);
    if (v) gewaehlt.push(v);
  }
  return gewaehlt;
}

/* `einsatzMonate` ist am 28.09.2026 nach `lib/frequenz.ts` umgezogen: es
 * braucht jetzt den Katalog, um Nebenkosten auszulassen und `folgt` zu
 * bedienen (Mig 187). */

/** Katalogeintrag → Vertragsposition. `monate` ist absichtlich leer
 *  voreingestellt: die Standardmonate des Katalogs sind ein Anhaltspunkt für
 *  die Planung, keine Zusage an den Kunden. Wer sie übernehmen will, gibt sie
 *  ausdrücklich mit (Legacy-Übernahme, Anfahrten). */
/**
 * Ohne ausdrückliche Monate kommt eine Position mit den üblichen Monaten ihres
 * Katalogeintrags (`standard_monate`). Bis zum 28.09.2026 kam sie ohne — und
 * zählte damit null Mal im Jahr: Jahreswert 0 €, obwohl der Hinweis darunter
 * „zählt vorerst mit einem Durchgang" versprach (Benjamin: „Zahlen sind
 * falsch"). Einträge ohne Standardmonate (Anfahrt, Entsorgung, Stunden)
 * bleiben leer; Anfahrt und Entsorgung ziehen ihre Monate ohnehin nach.
 */
export function positionAusKatalog(
  k: KatalogEintrag, menge: number = 1,
  monate: number[] = (k.standard_monate || []).filter(m => m >= 1 && m <= 12),
): VertragPosition {
  return {
    leistung_id: k.id, titel_override: null, menge, einheit: k.einheit,
    einzelpreis_netto: Number(k.vk_netto), kosten_snapshot: Number(k.kosten_netto),
    zeit_min_snapshot: Number(k.zeit_min_je_einheit), lohnanteil_prozent: Number(k.lohnanteil_prozent),
    intervall: "x_pro_jahr", monate: [...monate].sort((a, b) => a - b),
    optional: false, beauftragt: true, material_notiz: "", sort_order: 0,
  };
}

/** Position ohne Katalogbezug — für Leistungen, die es (noch) nicht gibt.
 *  Preis, Kosten und Zeit stehen auf null: sie sind bewusst leer statt geraten,
 *  damit im Deckungsbeitrag sichtbar bleibt, dass hier noch etwas fehlt. */
export function freiePosition(titel: string): VertragPosition {
  return {
    leistung_id: null, titel_override: titel.trim(), menge: 1, einheit: "pauschal",
    einzelpreis_netto: 0, kosten_snapshot: 0, zeit_min_snapshot: 0, lohnanteil_prozent: 100,
    intervall: "x_pro_jahr", monate: [],
    optional: false, beauftragt: true, material_notiz: "", sort_order: 0,
  };
}

/** Titel, unter dem eine Position im Vertrag steht — eigener Text schlägt den
 *  Katalog. Eine Stelle dafür, damit Anzeige, Katalog-Abgleich und
 *  Katalog-Aufnahme nicht auseinanderlaufen.
 *
 *  `null` heißt „kein eigener Titel, nimm den Katalog"; ein LEERER eigener
 *  Titel bleibt leer. Der Unterschied ist nicht theoretisch: sonst spränge beim
 *  Löschen des letzten Zeichens der Katalogtitel zurück ins Feld, und niemand
 *  könnte den Namen überschreiben. */
export function positionTitel(
  p: { titel_override: string | null; leistung_id: string | null },
  katalog: KatalogEintrag[],
): string {
  if (p.titel_override !== null && p.titel_override !== undefined) return p.titel_override;
  return katalog.find(k => k.id === p.leistung_id)?.titel || "";
}

/** Gespeicherte Position (Server, Zahlen als Text) → Wizard-Position.
 *  Gegenstück zu positionAusKatalog für das Fortsetzen eines Entwurfs. */
export function positionAusGespeichert(l: {
  leistung_id: string | null; titel_override: string | null; menge: string;
  einheit: string; einzelpreis_netto: string; kosten_snapshot: string;
  zeit_min_snapshot: string; lohnanteil_prozent: string; intervall: string;
  monate: number[]; optional: boolean;
  beauftragt: boolean; material_notiz: string; sort_order: number;
  folgt?: string[] | null;
}): VertragPosition {
  return {
    leistung_id: l.leistung_id, titel_override: l.titel_override,
    menge: Number(l.menge), einheit: l.einheit,
    einzelpreis_netto: Number(l.einzelpreis_netto),
    kosten_snapshot: Number(l.kosten_snapshot),
    zeit_min_snapshot: Number(l.zeit_min_snapshot),
    lohnanteil_prozent: Number(l.lohnanteil_prozent),
    intervall: l.intervall === "monatlich" ? "monatlich" : "x_pro_jahr",
    monate: l.monate || [],
    optional: l.optional, beauftragt: l.beauftragt,
    material_notiz: l.material_notiz || "", sort_order: l.sort_order || 0,
    folgt: l.folgt ?? null,
  };
}

export function positionenAusLegacy(jsonRaw: string | null | undefined, katalog: KatalogEintrag[]): VertragPosition[] {
  if (!jsonRaw) return [];
  let parsed: Record<string, string>;
  try { parsed = JSON.parse(jsonRaw); } catch { return []; }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
  const keywordMap: Array<[RegExp, string]> = [
    [/hecke/i, "HECKE_SCHNITT_LFM"], [/obstbaum|obstgehölz/i, "OBSTBAUM_SCHNITT"],
    [/baum|bäume/i, "BAUM_SCHNITT_GROSS"], [/strauch|sträucher|gehölz/i, "STRAUCH_SCHNITT"],
    [/rasen|mähen/i, "RASEN_MAEHEN_QM"], [/vertikutier/i, "VERTIKUTIEREN_QM"],
    [/beet|unkraut/i, "BEET_PFLEGE_QM"], [/laub/i, "LAUB_QM"],
    [/gieß|wässer|bewässer/i, "GIESSGANG"], [/düng/i, "RASEN_DUENGEN_QM"],
    [/teich/i, "STUNDE_GAERTNER"], [/pflege/i, "STUNDE_GAERTNER"],
  ];
  const byCode = new Map(katalog.map(k => [k.code, k]));
  const gruppen = new Map<string, { text: string; monate: number[] }>();
  for (const [monatStr, text] of Object.entries(parsed)) {
    const monat = Number(monatStr);
    if (!monat || monat < 1 || monat > 12) continue;
    const key = String(text).trim().toLowerCase();
    const g = gruppen.get(key) || { text: String(text).trim(), monate: [] };
    g.monate.push(monat);
    gruppen.set(key, g);
  }
  const out: VertragPosition[] = [];
  for (const g of gruppen.values()) {
    const treffer = keywordMap.find(([re]) => re.test(g.text));
    const kat = treffer ? byCode.get(treffer[1]) : undefined;
    const monate = [...g.monate].sort((a, b) => a - b);
    if (kat) {
      // `material_notiz` ist die Beschreibung, die der Kunde liest — kein
      // Platz für Hinweise ans Büro. Der alte Aufgabentext ist eine.
      out.push({ ...positionAusKatalog(kat, 1, monate), material_notiz: g.text });
    } else {
      out.push({
        leistung_id: null, titel_override: g.text, menge: 1, einheit: "pauschal",
        einzelpreis_netto: 0, kosten_snapshot: 0, zeit_min_snapshot: 0, lohnanteil_prozent: 100,
        intervall: "x_pro_jahr", monate,
        optional: false, beauftragt: true,
        // Preis 0 zeigt die Kalkulation ohnehin als offen an.
        material_notiz: "", sort_order: 0,
      });
    }
  }
  return out;
}

/** Anschrift eines Kunden in die drei Felder der Objektanschrift zerlegt.
 *
 *  `adresse` ist die Lieferadresse, wenn es eine gibt (dort liegt meist der
 *  Garten), sonst „Straße, PLZ Ort" aus den Stammdaten. Lieferadressen sind
 *  freier Text, meist mehrzeilig, manchmal mit Namen davor:
 *  „Familie X⏎Musterweg 3⏎01099 Dresden". Deshalb: die Zeile mit der PLZ
 *  suchen, die Zeile davor ist die Straße. Ohne PLZ-Zeile gelten die
 *  Stammdatenfelder. */
export function anschriftTeile(k: KundeLite): { strasse: string; plz: string; ort: string } {
  const zeilen = (k.adresse || "").split(/\n|,/).map(z => z.trim()).filter(Boolean);
  const i = zeilen.findIndex(z => /^\d{5}\s+\S/.test(z));
  if (i >= 0) {
    const m = zeilen[i].match(/^(\d{5})\s+(.+)$/);
    return {
      strasse: i > 0 ? zeilen[i - 1] : (k.strasse || ""),
      plz: m ? m[1] : "", ort: m ? m[2].trim() : "",
    };
  }
  return { strasse: k.strasse || zeilen[0] || "", plz: k.plz || "", ort: k.ort || "" };
}

export type KundeLite = {
  id: string; name: string; ort: string; adresse: string; email: string;
  /** Einzeln, damit der Wizard die Objektanschrift vorbelegen kann (28.09.2026). */
  strasse?: string; plz?: string;
};
